class AlgoliaAutocomplete extends HTMLElement {
  constructor() {
    super();
    this.beforeAutocompleteFooterTemplate = this.beforeAutocompleteFooterTemplate.bind(this);
    this.beforeAutocompleteHeaderTemplate = this.beforeAutocompleteHeaderTemplate.bind(this);
    this.beforeAutocompleteMainProductsTemplate = this.beforeAutocompleteMainProductsTemplate.bind(this);
    this.beforeAutocompleteMainTemplate = this.beforeAutocompleteMainTemplate.bind(this);
    this.beforeAutocompleteNoResultsTemplate = this.beforeAutocompleteNoResultsTemplate.bind(this);
    this.beforeAutocompleteOptions = this.beforeAutocompleteOptions.bind(this);
    this.beforeAutocompleteProductTemplate = this.beforeAutocompleteProductTemplate.bind(this);
    this.connectedCallback = this.connectedCallback.bind(this);
    this.getCatalogItemType = this.getCatalogItemType.bind(this);
    this.getColorCode = this.getColorCode.bind(this);
    this.getGender = this.getGender.bind(this);
    this.getParentSku = this.getParentSku.bind(this);
    this.hideSearch = this.hideSearch.bind(this);
    this.onMount = this.onMount.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.subscribe = this.subscribe.bind(this);
  }

  /**
   * Template function for customizing the footer of autocomplete suggestions.
   * @param {function} defaultTemplate - Default template function provided by the autocomplete library.
   * @param {object} templateOptions - Options object containing HTML template and state information.
   * @returns {string} Customized HTML template for autocomplete footer.
   */
  beforeAutocompleteFooterTemplate(_defaultTemplate, templateOptions) {
    const { html, state } = templateOptions;
    const href = `${window.Shopify.routes.root}search?q=${state.query}`;
    return html`
      <li role="option">
        <a class="predictive-search__item predictive-search__item--term link link--text p-regular p-link animate-arrow" href="${href}" tabindex="-1">
          ${this.allResults}
        </a>
      </li>
    `;
  }

  /**
   * Template function for customizing the header of autocomplete suggestions.
   * @param {function} defaultTemplate - Default template function provided by the autocomplete library.
   * @param {object} templateOptions - Options object containing HTML template.
   * @param {object} _resource - Additional resource parameter (not used in this function).
   * @returns {string} Customized HTML template for autocomplete header.
   */
  beforeAutocompleteHeaderTemplate(_defaultTemplate, templateOptions, _resource) {
    const { html } = templateOptions;
    return html`
      <div class="aa-SourceHeader">
        <div class="aa-SourceHeaderLine"></div>
      </div>
    `;
  }

  /**
   * Template function for customizing the main product section of autocomplete suggestions.
   * @param {function} defaultTemplate - Default template function provided by the autocomplete library.
   * @param {object} templateOptions - Options object containing HTML template and elements.
   * @param {object} elements - Elements object containing products for display.
   * @returns {string} Customized HTML template for main products in autocomplete.
   */
  beforeAutocompleteMainProductsTemplate(_defaultTemplate, templateOptions, elements) {
    const { html } = templateOptions;
    return html`<div>${elements.products}</div>`;
  }

  /**
   * Template function for customizing the main autocomplete suggestions section.
   * @param {function} defaultTemplate - Default template function provided by the autocomplete library.
   * @param {object} templateOptions - Options object containing HTML template and elements.
   * @param {object} elements - Elements object containing products for display.
   * @param {function} _displaySuggestions - Function for displaying suggestions (not used in this function).
   * @returns {string} Customized HTML template for autocomplete suggestions.
   */
  beforeAutocompleteMainTemplate(_defaultTemplate, templateOptions, elements, _displaySuggestions) {
    const { html } = templateOptions;
    return html`
      <ul id="predictive-search-results-list" class="predictive-search__results-list list-unstyled">
        ${elements.products}
      </ul>
    `;
  }

  /**
   * Template function for customizing the autocomplete suggestions when no results are found.
   * @param {function} defaultTemplate - Default template function provided by the autocomplete library.
   * @param {object} templateOptions - Options object containing HTML template.
   * @returns {string} Customized HTML template for no results found in autocomplete.
   */
  beforeAutocompleteNoResultsTemplate(_defaultTemplate, templateOptions) {
    const { html } = templateOptions;
    return html`
      <ul id="predictive-search-results-list" class="predictive-search__results-list list-unstyled">
        <p class="p-small predictive-search__results-list--empty">${this.emptySearch}</p>
      </ul>
    `;
  }

  /**
   * Hook function executed before initializing the autocomplete options.
   * @param {object} options - Autocomplete options object to be customized.
   * @returns {object} Modified autocomplete options object.
   */
  beforeAutocompleteOptions(options) {
    const searchIcon = document.querySelector('algolia-icon-search');
    const searchIconStylesContent = searchIcon ? searchIcon.getStyle() : '';

    const iconCaretLeftStyles = this.querySelector('algolia-icon-caret-left');
    const iconCaretLeftStylesContent = iconCaretLeftStyles ? iconCaretLeftStyles.getStyle() : '';
    let classNames;
    const isMobile = window.matchMedia(`(max-width: ${this.wideBreakpoint}px)`).matches;

    const baseClasses = {
      input: 'search__input searchfield__input p-regular',
      label: 'search__button searchfield__button',
      list: 'predictive-search__results-list list-unstyled',
      loadingIndicator: 'hidden',
      panel: `predictive-search predictive-search--inline ${this.productSuggestionsEnabled ? "" : "hidden"}`,
      root: 'search-box-container',
      sourceFooter: 'list-unstyled',
      sourceHeader: 'hidden',
      submitButton: `icon icon-search`,
      clearButton: `icon icon-caret-left collapse-button close-search ${ this.searchVariant === 'expandable' ? '' : 'hidden'}`,
    }

    if (this.searchVariant === 'expandable' && !isMobile) {
      const predictiveSearch = document.querySelector('predictive-search form');
      if (predictiveSearch) {
        options.panelContainer = predictiveSearch;
        options.panelPlacement = 'full-width';
      }
      classNames = {
        ...baseClasses,
        form: 'search-box',
      };
    }

    if (this.searchVariant === 'expandable' && isMobile) {
      const predictiveSearch = document.querySelector('predictive-search form');
      if (predictiveSearch) {
        options.panelContainer = predictiveSearch;
        options.panelPlacement = 'full-width';
      }
      classNames = {
        ...baseClasses,
        form: 'search-box',
      };
    }

    if (this.searchVariant !== 'expandable' && !isMobile) {
      const fullWidthMountPoint = document.querySelector('algolia-autocomplete-mount-point#algolia-desktop-full-search-bar');
      const panelWrapper = fullWidthMountPoint.querySelector('.search-modal__form');
      options.container = panelWrapper;
      options.panelContainer = panelWrapper;
      options.panelPlacement = 'full-width';
      classNames = {
        form: 'searchfield search-box',
        ...baseClasses
      };
    }

    // mobile search flyout selectors
    if (this.searchVariant === 'search_flyout' && isMobile) {
      const flyoutMountPoint = document.querySelector('algolia-autocomplete-mount-point#algolia-autocomplete-flyout-search');
      const panelWrapper = flyoutMountPoint.querySelector('.search-modal__form');
      options.container = panelWrapper;
      options.panelContainer = panelWrapper;
      options.panelPlacement = 'input-wrapper-width';
      classNames = {
        form: 'searchfield',
        ...baseClasses,
        root: 'header__inline-search search--mobile'
      };
    }else if(this.searchVariant === 'full_search_bar' && isMobile) {
      const inlineMountPoint = document.querySelector('algolia-autocomplete-mount-point#algolia-mobile-full-search-bar');
      const panelWrapper = inlineMountPoint.querySelector('.search-modal__form');
      options.container = panelWrapper;
      options.panelContainer = panelWrapper;
      options.panelPlacement = 'full-width';
      classNames = {
        form: 'searchfield  search-box',
        ...baseClasses
      };
    }

    options.classNames =  classNames;
    options.detachedMediaQuery = 'none';
    options.onSubmit = this.onSubmit;
    options.onReset = this.hideSearch;
    options.placeholder = this.search;
    if (this.searchVariant === 'expandable' && iconCaretLeftStylesContent)  options.container.insertAdjacentHTML('afterbegin', iconCaretLeftStylesContent.outerHTML);
    if (searchIconStylesContent) options.container.insertAdjacentHTML('afterbegin', searchIconStylesContent.outerHTML);
    return options;
  }

  /**
   * Template function for customizing the product template in autocomplete suggestions.
   * @param {function} defaultTemplate - Default template function provided by the autocomplete library.
   * @param {object} templateOptions - Options object containing HTML template and item data.
   * @param {object} distinct - Limit the number of duplicate records.
   * @param {string} itemLink - URL link for the product item.
   * @returns {string} Customized HTML template for displaying a product in autocomplete suggestions.
   */
  beforeAutocompleteProductTemplate(_defaultTemplate, templateOptions, _distinct, itemLink) {
    const { html, item } = templateOptions;

    /* Metadata. */
    const catalogItemType = this.getCatalogItemType(item);
    const gender = this.getGender(item);
    const colorCode = this.getColorCode(item);
    const parentSku = this.getParentSku(item);

    const image = item.product_image || this.mediaCameraShy;
    const compactedImage = this.algoliaShopify.helpers.compactImage({ image });
    const isMixedStyles = catalogItemType.includes('OPEN_SKU');

    /* Elements. */
    const algoliaPrice = this.querySelector('algolia-price');
    const productBadgeElement = this.querySelector('algolia-product-badge');
    const mixedStylesCardProductElement = this.querySelector('algolia-mixed-styles-card-product');

    /* Content. */
    const algoliaPriceContent = algoliaPrice ? algoliaPrice.render(item) : '';
    const mixedStylesCardProductElements = mixedStylesCardProductElement ? mixedStylesCardProductElement.render(colorCode, parentSku) : [];
    const mixedStylesCardProductContent = mixedStylesCardProductElements.length ? mixedStylesCardProductElements.map(element => element.outerHTML) : '';

    const productBadgeElements = productBadgeElement ? productBadgeElement.render(this.mixedStyles) : [];
    const productBadgeContent = productBadgeElements.length ? productBadgeElements.map(element => element.outerHTML) : '';

    return html`
      <li id="predictive-search-option-${item.id}" class="predictive-search__list-item" role="option" aria-selected="false" data-acsb-tooltip-bound="true">
        <div class="card-wrapper product-card-wrapper">
          <div class="card">
            <a href="${itemLink}" class="predictive-search__item predictive-search__item--link link link--text">
              ${isMixedStyles && productBadgeContent ? html([`
                <style>
                  .predictive-search__item .product__badge {
                    position: absolute;
                    padding: ${this.productBadgePaddingYCard }px ${this.productBadgePaddingXCard }px;
                  }
                </style>
                <div id="product__badge" class="product__badge ${this.productBadgeTextSizeProductCard} ${this.productBadgeVerticalPosition} ${this.productBadgeHorizontalPosition}">
                  ${ html([productBadgeContent]) }
                </div>
              `]) : ""}
              ${isMixedStyles && mixedStylesCardProductContent ?
                html(mixedStylesCardProductContent) :
              image ? html`
                <img
                  class="predictive-search__image"
                  src="${compactedImage}"
                  alt="${item.title}"
                  width="80"
                  height="80"
                />` : ""}
              <div class="
                predictive-search__item-content
                ${!this.productSuggestionsShowVendor && !this.productSuggestionsShowPrice ? "predictive-search__item-content--centered" : ""}
              ">
                ${this.productSuggestionsShowVendor ? html`
                  <span class="visually-hidden">${this.vendor}</span>
                  <div class="predictive-search__item-vendor caption-with-letter-spacing">${item.vendor}</div>
                ` : ""}
                <h3 class="predictive-search__item-heading p-small p-bold line-clamp-1 ${this.typeProductCardNameAllCaps ? "p-all-caps" : ""}">${item.title}</h3>
                ${(this.typeProductCardShowGender && gender) ? html`
                  <h3 class="predictive-search__item-color p-small">${gender}</h3>
                ` : ""}
                ${this.productSuggestionsShowPrice && algoliaPriceContent ? html([algoliaPriceContent.outerHTML]) : ""}
              </div>
            </a>
          </div>
        </div>
      </li>
    `;
  }

  /**
   * Initializes settings based on custom attributes when the element is connected to the DOM.
   * @returns {void}
   */
  connectedCallback() {
    this.allResults = this.getAttribute('all-results');
    this.emptySearch = this.getAttribute('empty-search');
    this.mediaCameraShy = this.getAttribute('media-camera-shy');
    this.mixedStyles = this.getAttribute('mixed-styles');
    this.productBadgeHorizontalPosition = this.getAttribute('product-badge-horizontal-position');
    this.productBadgePaddingYCard = this.getAttribute('product-badge-padding-y-card');
    this.productBadgePaddingXCard = this.getAttribute('product-badge-padding-x-card');
    this.productBadgeTextSizeProductCard = this.getAttribute('product-badge-text-size-product-card');
    this.productBadgeVerticalPosition = this.getAttribute('product-badge-vertical-position');
    this.productSuggestionsEnabled = this.getAttribute('product-suggestions-enabled').toLowerCase() === 'true';
    this.productSuggestionsShowPrice = this.getAttribute('product-suggestions-show-price').toLowerCase() === 'true';
    this.productSuggestionsShowVendor = this.getAttribute('product-suggestions-show-vendor').toLowerCase() === 'true';
    this.search = this.getAttribute('search');
    this.typeProductCardShowGender = this.getAttribute('type-product-card-show-gender').toLowerCase() === 'true';
    this.typeProductCardNameAllCaps = this.getAttribute('type-product-card-name-all-caps').toLowerCase() === 'true';
    this.vendor = this.getAttribute('vendor');

    this.searchVariant = SEARCH_CONFIG.displayVariant;
    this.wideBreakpoint = SEARCH_CONFIG.wideBreakpoint;
    this.onMount();
  }

  /**
   * Retrieves the catalog item type from the given item's metadata.
   * @param {Object} item - The item object containing metadata.
   * @returns {string} The extracted catalog item type from the item's metadata.
   */
  getCatalogItemType(item) {
    const troveFacets = item?.meta['trove-facets'];
    const catalogItemType = troveFacets?.catalogItemType?.toString() || '';
    return catalogItemType;
  }

  /**
   * Retrieves the parent sku from the given item's metadata.
   * @param {Object} item - The item object containing metadata.
   * @returns {string} The extracted parent sku from the item's metadata.
   */
  getColorCode(item) {
    const troveProduct = item?.meta['trove-product'];
    const colorCode = troveProduct?.color_code?.toString() || '';
    return colorCode;
  }

  /**
   * Retrieves the gender from the given item's metadata.
   * @param {Object} item - The item object containing metadata.
   * @returns {string} The extracted gender from the item's metadata.
   */
  getGender(item) {
    const troveFacets = item?.meta['trove-facets'];
    const troveGender = troveFacets?.gender?.toString() || '';
    const gender = troveGender.replace("'s", '').replace('["', '').replace('"]', '');
    return gender;
  }

  /**
   * Retrieves the parent sku from the given item's metadata.
   * @param {Object} item - The item object containing metadata.
   * @returns {string} The extracted parent sku from the item's metadata.
   */
  getParentSku(item) {
    const troveProduct = item?.meta['trove-product'];
    const parentSku = troveProduct?.parent_sku?.toString() || '';
    return parentSku;
  }

  /**
   * Hides the expandable search component and shows the expandable search button.
   * @returns {void}
   */
  hideSearch() {
    if(this.searchVariant === 'expandable') {
      const expandableSearch = document.querySelector('expandable-search');
      if (expandableSearch) expandableSearch.classList.add('hidden');

      const expandableSearchButton = document.querySelector('#expandable-search-button');
      if (expandableSearchButton) expandableSearchButton.classList.remove('visibility-hidden', 'hidden');
    }
  }

  /**
   * Registers an event listener for the 'algolia.hooks.initialize' event when the component mounts.
   * @returns {void}
   */
  onMount() {
    document.addEventListener('algolia.hooks.initialize', this.subscribe);
  }

  /**
   * Redirects to search page if query exists, hides search component if not.
   * @param {object} params - The parameters object containing search state.
   * @returns {void}
   */
  onSubmit(params) {
    const uri = params?.state?.query?.trim() || '';

    const query = encodeURIComponent(uri);
    // if (!query && this.searchVariant === 'expandable') return this.hideSearch();
    if (!query) return;

    window.location = `${window.Shopify.routes.root}search?q=${query}`;
  }

  /**
   * Subscribes to Algolia hooks and sets up necessary configurations.
   * @returns {void}
   */
  subscribe() {
    this.algoliaShopify = window.algoliaShopify;
    if (!this.algoliaShopify) return;

    this.algoliaShopify.hooks.registerHook('beforeAutocompleteFooterTemplate', this.beforeAutocompleteFooterTemplate);
    this.algoliaShopify.hooks.registerHook('beforeAutocompleteHeaderTemplate', this.beforeAutocompleteHeaderTemplate);
    this.algoliaShopify.hooks.registerHook('beforeAutocompleteMainProductsTemplate', this.beforeAutocompleteMainProductsTemplate);
    this.algoliaShopify.hooks.registerHook('beforeAutocompleteMainTemplate', this.beforeAutocompleteMainTemplate);
    this.algoliaShopify.hooks.registerHook('beforeAutocompleteNoResultsTemplate', this.beforeAutocompleteNoResultsTemplate);
    this.algoliaShopify.hooks.registerHook('beforeAutocompleteOptions', this.beforeAutocompleteOptions);
    this.algoliaShopify.hooks.registerHook('beforeAutocompleteProductTemplate', this.beforeAutocompleteProductTemplate);
  }
}

customElements.define("algolia-autocomplete", AlgoliaAutocomplete);